import React from 'react';
import type { DemandaEsteira } from '../../../types';

// UC do cliente no card e na tabela do Painel de Cobrança (#6201), com link para
// as faturas (#6214). Abre noutra aba para não perder o lugar no painel. O nome
// do cliente continua abrindo a negociação — decisão de 09/10/2026.

/**
 * Link de Faturas e Cobranças já filtrado (a busca aceita vírgula entre termos),
 * em todos os meses — sem `meses=todos` vale o mês gravado no navegador e a
 * fatura vencida há dois meses não aparece. Igual à Rota de Cobrança.
 */
export function linkFaturas(termos: string[]): string {
    return `#/financeiro/faturas?busca=${encodeURIComponent(termos.join(','))}&meses=todos`;
}

/** Acima disto o "+N" abre pelo CPF: URL e busca com centenas de UCs pesam. */
const MAX_UCS_NO_LINK = 20;
const MAX_UCS_NO_TITULO = 15;

// Clique e tecla: na tabela a linha inteira abre a Lista com Enter/Espaço e
// cancelava o link.
const parar = (e: React.SyntheticEvent) => e.stopPropagation();
// O primary (#5F6C37) some sobre o fundo escuro (2,5:1 no gray-800); no escuro o hover
// usa o primary-light. Foco visível só para teclado (focus-visible), como anel sólido.
const classeFoco = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:focus-visible:ring-primary-light';
const classeHover = 'transition-colors hover:text-primary dark:hover:text-primary-light';
// nowrap: o número da UC nunca quebra de "UC" nem do "+N" (a célula da tabela quebra de linha em vez de cortar).
const classeLink = `rounded-sm whitespace-nowrap font-medium text-gray-700 dark:text-gray-200 underline decoration-dotted underline-offset-2 ${classeHover} ${classeFoco}`;
// Sem tamanho próprio: herda os 12px do card e os 10px da tabela, alinhado ao texto ao lado.
// O fundo cinza é o que diferencia a pílula do texto ao lado; sem o hover ela parecia contador, não link.
const classePilula = `ml-1 rounded whitespace-nowrap bg-gray-100 dark:bg-gray-700 px-1 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 ${classeHover} ${classeFoco}`;

/**
 * " · UC 1234 +2": a UC abre só as faturas dela; o "+2" abre as de todas as UCs
 * do card. Sem UC nenhuma (CPF fora do cadastro e das faturas), "ver faturas
 * pelo CPF". `separador` = começa com " · " (no card, depois do CPF).
 */
const UcsDoCard: React.FC<{ d: DemandaEsteira; separador?: boolean }> = ({ d, separador = true }) => {
    const ucs = d.ucs ?? (d.uc_numero ? [{ numero: d.uc_numero }] : []);
    const cpf = (d.cpf_cnpj_cliente ?? '').replace(/\D/g, '');
    const inicio = separador ? ' · ' : null;

    if (!ucs.length) {
        if (!cpf) return null;
        // 14 dígitos é CNPJ: "pelo CPF" num cliente pessoa jurídica parecia erro de cadastro.
        const doc = cpf.length > 11 ? 'CNPJ' : 'CPF';
        return (
            <>
                {inicio}
                <a href={linkFaturas([cpf])} target="_blank" rel="noopener noreferrer" onClick={parar} onKeyDown={parar}
                    title={`Nenhuma UC encontrada para este ${doc}, nem no cadastro nem nas faturas — abrir a busca de faturas por ele em nova aba (pode vir vazia)`}
                    className={classeLink}>
                    ver faturas pelo {doc}<span className="sr-only"> (abre em nova aba)</span>
                </a>
            </>
        );
    }

    const [primeira, ...resto] = ucs;
    const rotulo = (u: { numero: string; cancelada?: boolean }) => `UC ${u.numero}${u.cancelada ? ' (cancelada)' : ''}`;
    const pelaCpf = ucs.length > MAX_UCS_NO_LINK && !!cpf;
    const todas = pelaCpf ? linkFaturas([cpf]) : linkFaturas(ucs.map(u => u.numero));
    const listaTitulo = ucs.slice(0, MAX_UCS_NO_TITULO).map(rotulo).join('\n')
        + (ucs.length > MAX_UCS_NO_TITULO ? `\n… e mais ${ucs.length - MAX_UCS_NO_TITULO}` : '');
    // Com mais de uma UC o clique na primeira abre só as dela: o tooltip e o leitor
    // de tela dizem isso e apontam o "+N", que sem isso ninguém descobria.
    const soEstaUc = resto.length > 0;
    return (
        <>
            {inicio}
            <a href={linkFaturas([primeira.numero])} target="_blank" rel="noopener noreferrer" onClick={parar} onKeyDown={parar}
                title={soEstaUc
                    ? `Abrir só as faturas da ${rotulo(primeira)} em nova aba (o +${resto.length} abre as de todas as UCs)`
                    : `Abrir as faturas da ${rotulo(primeira)} em nova aba`}
                className={classeLink}>
                {rotulo(primeira)}<span className="sr-only"> — abrir {soEstaUc ? 'só as faturas desta UC' : 'faturas'} em nova aba</span>
            </a>
            {resto.length ? (
                <a href={todas} target="_blank" rel="noopener noreferrer" onClick={parar} onKeyDown={parar}
                    title={`${listaTitulo}\n\n${pelaCpf
                        ? `Abrir as faturas pelo CPF/CNPJ do cliente (${ucs.length} UCs) em nova aba`
                        : `Abrir as faturas de todas as ${ucs.length} UCs em nova aba`}`}
                    className={classePilula}>
                    +{resto.length}<span className="sr-only"> outras UCs — abrir as faturas de todas em nova aba</span>
                </a>
            ) : null}
        </>
    );
};

export default UcsDoCard;
